/**
 * Fix 3 (WP-577 review round 3, TEN-2918 follow-up) + Fix 2/3 (P0 review round 4 follow-up):
 * a batch preview's per-item `chain.createEntry` preview call reads the database independently
 * of every OTHER item in the same preview batch — nothing is ever written — so a real (non-preview)
 * run's ordering effects go unpredicted unless this connector-side tracker reproduces them:
 *
 *  - **Name collision** (original Fix 3): two items sharing a (collection, name) both come back
 *    `wouldCapture`, while a real run writes the first and rejects the second via
 *    `assertNoDuplicateEntryName` (convex/agentKnowledge/createEntryDuplicates.ts) — an EXACT
 *    string match on `name`, no case-folding, no whitespace trimming, scoped PER COLLECTION.
 *  - **entryId collision** (round 4, Fix 2): two items with DIFFERENT names but the SAME
 *    caller-supplied `entryId` both come back `wouldCapture`, while a real run rejects the
 *    second via `assertNoDuplicateEntryId` — a check that is **workspace-wide**, not scoped to
 *    any collection. This tracker mirrors that scope exactly: `isDuplicateEntryId`/
 *    `recordEntryId` take no collection argument, unlike the name predicate.
 *
 * This is connector-side bookkeeping over the CALLER'S OWN payload (acceptable per STD-2's
 * kernel/connector split) — it never re-implements a kernel policy decision, it only predicts
 * the ordering effect of the caller's own batch on the exact-match checks the kernel already
 * performs.
 *
 * Usage contract (see `checkBatchPreviewDuplicate`/`recordBatchPreviewIfConfirmed` below, and
 * their call sites in tools/smart-capture.ts's `handleBatchCapture` loop): check BEFORE issuing
 * this item's `chain.createEntry` preview call, and only record AFTER that call comes back
 * reporting the item would NOT be blocked. Recording eagerly (before knowing the outcome) would
 * flag a later item as a duplicate of an entry that itself would never be written (blocked or
 * failed) — a false positive the review explicitly ruled out ("prefer FALSE-NEGATIVE over
 * false-positive: never wrongly reject something the real run would accept").
 *
 * Name collisions MUST be recorded under the EFFECTIVE collection the server actually routed the
 * item to (`chain.createEntry`'s preview-branch `collection` field — see convex/agentKnowledge/
 * createEntryPreview.ts), never the caller's own `collectionSlug` argument: `prepareCreateEntry`
 * can redirect a write (e.g. `collectionSlug: "chains"` + a bet-shaped payload routes to
 * "work-packages"), and recording under the caller's pre-routing slug would miss a later item
 * whose OWN caller-facing slug already matches the routed destination (round 4, Fix 3).
 */

export type BatchPreviewDuplicateTracker = {
  /** True when (collection, name) was already `recordName`-ed earlier in this same preview batch. */
  isDuplicateName(collection: string, name: string): boolean;
  /** Record that (collection, name) would actually capture — call ONLY once confirmed non-blocked. Key `collection` on the EFFECTIVE (post-routing) destination, not the caller's raw input. */
  recordName(collection: string, name: string): void;
  /** True when this caller-supplied `entryId` was already `recordEntryId`-ed earlier in this batch. Workspace-wide — mirrors `assertNoDuplicateEntryId`'s scope, no collection argument. */
  isDuplicateEntryId(entryId: string): boolean;
  /** Record that this caller-supplied `entryId` would actually capture — call ONLY once confirmed non-blocked. */
  recordEntryId(entryId: string): void;
};

export function createBatchPreviewDuplicateTracker(): BatchPreviewDuplicateTracker {
  const seenByCollection = new Map<string, Set<string>>();
  const seenEntryIds = new Set<string>();
  return {
    isDuplicateName(collection, name) {
      return seenByCollection.get(collection)?.has(name) ?? false;
    },
    recordName(collection, name) {
      let names = seenByCollection.get(collection);
      if (!names) {
        names = new Set();
        seenByCollection.set(collection, names);
      }
      names.add(name);
    },
    isDuplicateEntryId(entryId) {
      return seenEntryIds.has(entryId);
    },
    recordEntryId(entryId) {
      seenEntryIds.add(entryId);
    },
  };
}

/** Minimal shape of one `handleBatchCapture` per-entry result — matches BatchEntryResult's required fields. */
export type BatchPreviewDuplicateResult = {
  entryIdx: number;
  name: string;
  collection: string;
  entryId: string;
  ok: boolean;
  autoLinks: number;
  advisedLinks: number;
  status: "draft" | "committed" | "proposed" | "draft_on_failure";
  classifiedBy?: "llm" | "heuristic" | "explicit";
  confidence?: number;
  confidenceTier?: "high" | "medium" | "low";
  error: string;
};

/**
 * Builds the would-fail result for a batch-local NAME duplicate — lands in `failed`/
 * `failedEntries` via the same `results.filter((r) => !r.ok)` split every other batch failure
 * already uses, so `buildBatchPreviewFromResults` (lib/batchCaptureOutput.ts) reports it
 * honestly as would-fail rather than would-capture.
 */
export function buildBatchPreviewDuplicateResult(
  entryIdx: number,
  name: string,
  collection: string,
  classifiedBy: "llm" | "heuristic" | "explicit" | undefined,
  confidence: number | undefined,
  confidenceTier: "high" | "medium" | "low" | undefined,
): BatchPreviewDuplicateResult {
  return {
    entryIdx,
    name,
    collection,
    entryId: "",
    ok: false,
    autoLinks: 0,
    advisedLinks: 0,
    status: "draft",
    classifiedBy,
    confidence,
    confidenceTier,
    error: `Duplicate entry: "${name}" already claimed by an earlier item in this batch [${collection}] — the real (non-preview) run would write the first and reject this one.`,
  };
}

/**
 * Builds the would-fail result for a batch-local caller-supplied `entryId` duplicate (round 4,
 * Fix 2) — same `ok:false` shape as the name-duplicate result above, but the collision is on
 * `entryId` (workspace-wide, per `assertNoDuplicateEntryId`), not on (collection, name).
 */
export function buildBatchPreviewDuplicateEntryIdResult(
  entryIdx: number,
  name: string,
  collection: string,
  entryId: string,
  classifiedBy: "llm" | "heuristic" | "explicit" | undefined,
  confidence: number | undefined,
  confidenceTier: "high" | "medium" | "low" | undefined,
): BatchPreviewDuplicateResult {
  return {
    entryIdx,
    name,
    collection,
    entryId: "",
    ok: false,
    autoLinks: 0,
    advisedLinks: 0,
    status: "draft",
    classifiedBy,
    confidence,
    confidenceTier,
    error: `Duplicate entry: ID "${entryId}" already claimed by an earlier item in this batch (workspace-wide) — the real (non-preview) run would write the first and reject this one.`,
  };
}

/**
 * Runs both batch-local duplicate checks (entryId, then name — same precedence
 * `handleBatchCapture`'s loop uses) for one item BEFORE its `chain.createEntry` preview call.
 * Returns the would-fail result to push (and `continue` on), or null when the item should
 * proceed — collapses two hand-rolled `if` blocks at the call site into one.
 */
export function checkBatchPreviewDuplicate(
  tracker: BatchPreviewDuplicateTracker,
  args: {
    preview: boolean | undefined;
    entryIdx: number;
    name: string;
    collection: string;
    callerEntryId: string | undefined;
    classifiedBy: "llm" | "heuristic" | "explicit" | undefined;
    confidence: number | undefined;
    confidenceTier: "high" | "medium" | "low" | undefined;
  },
): BatchPreviewDuplicateResult | null {
  if (!args.preview) return null;
  if (args.callerEntryId && tracker.isDuplicateEntryId(args.callerEntryId)) {
    return buildBatchPreviewDuplicateEntryIdResult(
      args.entryIdx, args.name, args.collection, args.callerEntryId,
      args.classifiedBy, args.confidence, args.confidenceTier,
    );
  }
  if (tracker.isDuplicateName(args.collection, args.name)) {
    return buildBatchPreviewDuplicateResult(
      args.entryIdx, args.name, args.collection, args.classifiedBy, args.confidence, args.confidenceTier,
    );
  }
  return null;
}

/**
 * Records a CONFIRMED non-blocked item's (collection, name) and, when the caller supplied one,
 * `entryId` onto the tracker — a no-op when `wouldCapture` is false (see the module doc
 * comment's "prefer FALSE-NEGATIVE over false-positive" contract). `effectiveCollection` MUST be
 * the EFFECTIVE (post-routing) collection the server's preview reported, not the caller's raw slug.
 */
export function recordBatchPreviewIfConfirmed(
  tracker: BatchPreviewDuplicateTracker,
  args: { wouldCapture: boolean; effectiveCollection: string; name: string; callerEntryId: string | undefined },
): void {
  if (!args.wouldCapture) return;
  tracker.recordName(args.effectiveCollection, args.name);
  if (args.callerEntryId) tracker.recordEntryId(args.callerEntryId);
}

/**
 * P0 round 4 (fix 2/3, root): re-checks and records a batch item's outcome AFTER its
 * `chain.createEntry` preview call has come back — the counterpart to
 * `checkBatchPreviewDuplicate`'s BEFORE-the-call check, needed because that earlier check is
 * keyed on the caller's PRE-routing `collectionSlug`. It cannot see THIS item itself routing
 * into a DIFFERENT effective collection (e.g. `collectionSlug: "chains"` + a bet-shaped payload
 * routes to "work-packages") that an EARLIER item in the same batch already claimed under that
 * destination — a real (non-preview) run writes the earlier item and rejects this one via
 * `assertNoDuplicateEntryName`, scoped per (effective) collection.
 *
 * `args.wouldCapture` must already reflect this item's own would-block verdict (false for a
 * would-BLOCK item, which is never written either way and so can never collide) — this function
 * only adds the post-routing name recheck on top, and never double-checks/double-records: it
 * either returns a would-fail duplicate result (nothing recorded) or records the confirmed
 * capture (nothing returned), never both.
 */
export function reconcileBatchPreviewDuplicate(
  tracker: BatchPreviewDuplicateTracker,
  args: {
    wouldCapture: boolean;
    effectiveCollection: string;
    entryIdx: number;
    name: string;
    callerEntryId: string | undefined;
    classifiedBy: "llm" | "heuristic" | "explicit" | undefined;
    confidence: number | undefined;
    confidenceTier: "high" | "medium" | "low" | undefined;
  },
): BatchPreviewDuplicateResult | null {
  if (args.wouldCapture && tracker.isDuplicateName(args.effectiveCollection, args.name)) {
    return buildBatchPreviewDuplicateResult(
      args.entryIdx, args.name, args.effectiveCollection, args.classifiedBy, args.confidence, args.confidenceTier,
    );
  }
  recordBatchPreviewIfConfirmed(tracker, {
    wouldCapture: args.wouldCapture,
    effectiveCollection: args.effectiveCollection,
    name: args.name,
    callerEntryId: args.callerEntryId,
  });
  return null;
}
